import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle, ArrowLeft, Ban, CheckCircle2, PlayCircle, Search, Undo2, XCircle,
} from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { useAuth } from '../contexts/auth'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtDate, fmtDateTime, type Location } from '../lib/industria'
import {
  APPROVAL_STATUS_LABEL, ORDER_STATUS_BADGE, ORDER_STATUS_LABEL, fmtQtyPerUnit,
  type OrderDetail,
} from '../lib/producao'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

export function IndustriaOpDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { can } = usePermissions()
  const canEdit = can('industria-ops', 'edit')
  const isAdmin = user?.role === 'admin'

  const [error, setError] = useState<string | null>(null)
  const [consumeOpen, setConsumeOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)
  const [consume, setConsume] = useState({
    materialId: '', volumeCode: '', quantity: '', kind: 'consumo' as 'consumo' | 'perda', reason: '',
  })
  const [qtyProduced, setQtyProduced] = useState('')
  const [stockLocationId, setStockLocationId] = useState('')
  const [sendToStock, setSendToStock] = useState(true)

  const { data: order, isLoading } = useQuery<OrderDetail>({
    queryKey: ['producao', 'order', id],
    queryFn: async () => (await api.get(`/production-orders/${id}`)).data,
    enabled: !!id,
  })

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['stock', 'locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['producao', 'order', id] })
    queryClient.invalidateQueries({ queryKey: ['producao', 'orders'] })
    queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
  }

  const releaseMutation = useMutation({
    mutationFn: async (force: boolean) =>
      (await api.post(`/production-orders/${id}/release`, { force })).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível liberar')),
  })

  const consumeMutation = useMutation({
    mutationFn: async () => (await api.post(`/production-orders/${id}/consume`, {
      materialId: consume.volumeCode ? null : consume.materialId || null,
      volumeCode: consume.volumeCode.trim() || null,
      quantity: Number(consume.quantity),
      kind: consume.kind,
      reason: consume.reason.trim() || null,
    })).data,
    onSuccess: () => {
      invalidate()
      setConsume({ materialId: '', volumeCode: '', quantity: '', kind: 'consumo', reason: '' })
      setConsumeOpen(false)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível apontar')),
  })

  const reverseMutation = useMutation({
    mutationFn: async (consumptionId: string) =>
      (await api.post(`/production-consumptions/${consumptionId}/reverse`, {})).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível estornar')),
  })

  const closeMutation = useMutation({
    mutationFn: async () => (await api.post(`/production-orders/${id}/close`, {
      qtyProduced: Number(qtyProduced),
      locationId: stockLocationId || null,
      sendToStock,
    })).data,
    onSuccess: () => { invalidate(); setCloseOpen(false) },
    onError: (e) => setError(apiError(e, 'Não foi possível encerrar')),
  })

  const cancelMutation = useMutation({
    mutationFn: async (reason: string) =>
      (await api.post(`/production-orders/${id}/cancel`, { reason })).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível cancelar')),
  })

  const decideMutation = useMutation({
    mutationFn: async (v: { approvalId: string; status: 'approved' | 'rejected' }) =>
      (await api.patch(`/production-approvals/${v.approvalId}`, { status: v.status })).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível decidir')),
  })

  if (isLoading) return <p className="py-16 text-center text-sm text-gray-400">Carregando...</p>
  if (!order) return <p className="py-16 text-center text-sm text-gray-400">Ordem não encontrada.</p>

  const pending = order.approvals.filter(a => a.status === 'pending' || a.status === 'rejected')
  const canAppoint = ['released', 'in_progress', 'reported', 'pending_approval'].includes(order.status)
  const canClose = canAppoint && pending.length === 0

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/industria/ops" className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-3 w-3" /> Ordens
          </Link>
          <h2 className="font-mono text-xl font-semibold text-gray-900">{order.number}</h2>
          <p className="text-sm text-gray-600">
            {order.productName} <span className="font-mono text-gray-400">{order.productSku}</span> · {order.tenantName}
            {order.recipeVersion != null ? ` · ficha v${order.recipeVersion}` : ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant={ORDER_STATUS_BADGE[order.status]}>{ORDER_STATUS_LABEL[order.status]}</Badge>
            <span className="text-sm text-gray-600">
              {order.qtyPlanned.toLocaleString('pt-BR')} planejadas
              {order.qtyProduced ? ` · ${order.qtyProduced.toLocaleString('pt-BR')} produzidas` : ''}
            </span>
            {order.lotNumber && (
              <span className="rounded-lg bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                lote {order.lotNumber}
              </span>
            )}
            {order.expiryDate && (
              <span className="text-xs text-gray-500">validade {fmtDate(order.expiryDate)}</span>
            )}
            {order.stockEntryAt && (
              <Badge variant="success">
                no estoque WsFull{order.stockEntryLocation ? ` · ${order.stockEntryLocation}` : ''}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {order.lotNumber && (
            <Link to={`/industria/rastreabilidade?lote=${encodeURIComponent(order.lotNumber)}`}>
              <Button variant="secondary"><Search className="h-4 w-4" /> Rastrear</Button>
            </Link>
          )}
          {canEdit && order.status === 'planned' && (
            <Button loading={releaseMutation.isPending} onClick={() => { setError(null); releaseMutation.mutate(false) }}>
              <PlayCircle className="h-4 w-4" /> Liberar
            </Button>
          )}
          {canEdit && canAppoint && (
            <Button onClick={() => { setError(null); setConsumeOpen(true) }}>Apontar</Button>
          )}
          {canEdit && canAppoint && (
            <Button
              variant="secondary"
              onClick={() => { setError(null); setQtyProduced(String(order.qtyPlanned)); setCloseOpen(true) }}
            >
              <CheckCircle2 className="h-4 w-4" /> Encerrar
            </Button>
          )}
          {canEdit && ['planned', 'released'].includes(order.status) && (
            <Button
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Motivo do cancelamento:')
                if (reason) { setError(null); cancelMutation.mutate(reason) }
              }}
            >
              <Ban className="h-4 w-4" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      {pending.length > 0 && (
        <Card className="border-red-200 bg-red-50/40">
          <CardHeader className="border-red-100">
            <span className="flex items-center gap-2 text-sm font-semibold text-red-800">
              <AlertTriangle className="h-4 w-4" />
              Perda acima do teto — o encerramento fica travado até a decisão
            </span>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.map(a => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2">
                <div className="text-sm">
                  <span className="font-medium text-gray-900">{a.materialName}</span>
                  <span className="ml-2 font-mono text-gray-600">
                    perda {Number(a.qtyScrap).toLocaleString('pt-BR')} · teto {Number(a.qtyLimit).toLocaleString('pt-BR')}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">{APPROVAL_STATUS_LABEL[a.status]}</span>
                </div>
                {isAdmin ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={decideMutation.isPending}
                      onClick={() => decideMutation.mutate({ approvalId: a.id, status: 'rejected' })}
                    >
                      <XCircle className="h-4 w-4" /> Reprovar
                    </Button>
                    <Button
                      size="sm"
                      loading={decideMutation.isPending}
                      onClick={() => decideMutation.mutate({ approvalId: a.id, status: 'approved' })}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Aprovar
                    </Button>
                  </div>
                ) : (
                  <span className="text-xs text-gray-500">Só admin decide</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Ficha desta ordem</span>
          <span className="text-xs text-gray-500">previsto para {order.qtyPlanned.toLocaleString('pt-BR')} unidades</span>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Material</th>
                  <th className="px-4 py-2.5 text-right font-medium">Por unidade</th>
                  <th className="px-4 py-2.5 text-right font-medium">Previsto</th>
                  <th className="px-4 py-2.5 text-right font-medium">Consumido</th>
                  <th className="px-4 py-2.5 text-right font-medium">Perda</th>
                  <th className="px-4 py-2.5 text-right font-medium">Teto de perda</th>
                  <th className="px-4 py-2.5 text-right font-medium">Falta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {order.items.map(it => (
                  <tr key={it.id} className={it.overScrapLimit ? 'bg-red-50/40' : undefined}>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-900">{it.materialName}</span>
                      <span className="block font-mono text-xs text-gray-400">
                        {it.materialCode}{!it.required ? ' · opcional' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-gray-500">
                      {fmtQtyPerUnit(it.qtyPerUnit)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                      {it.need.toLocaleString('pt-BR')} <span className="text-xs text-gray-400">{it.unit}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                      {it.consumed.toLocaleString('pt-BR')}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono ${it.overScrapLimit ? 'font-semibold text-red-600' : 'text-gray-600'}`}>
                      {it.scrapped.toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-400">
                      {it.scrapLimit.toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                      {it.remaining.toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">
            Apontamentos <span className="font-normal text-gray-400">({order.consumptions.length})</span>
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {order.consumptions.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">
              Nada apontado — bipe o volume na tela de Apontamento
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Quando</th>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote / volume</th>
                    <th className="px-4 py-2.5 text-right font-medium">Qtd</th>
                    <th className="px-4 py-2.5 text-left font-medium">Tipo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Motivo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Quem</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {order.consumptions.map(c => (
                    <tr key={c.id} className={c.quantity < 0 ? 'text-gray-400' : undefined}>
                      <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(c.createdAt)}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{c.materialName}</span>
                        <span className="block font-mono text-xs text-gray-400">{c.materialCode}</span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                        {c.lotNumber ?? '—'}
                        {c.volumeCode && <span className="block text-gray-400">{c.volumeCode}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {c.quantity.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={c.kind === 'perda' ? 'danger' : 'default'}>{c.kind}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{c.reason ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{c.author ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && canAppoint && c.quantity > 0 && (
                          <button
                            title="Estornar (movimento oposto, o original fica)"
                            className="text-gray-300 hover:text-blue-600"
                            onClick={() => { setError(null); reverseMutation.mutate(c.id) }}
                          >
                            <Undo2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Apontar consumo ou perda" open={consumeOpen} onClose={() => setConsumeOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); consumeMutation.mutate() }}
        >
          <Input
            label="Volume bipado"
            className="font-mono"
            placeholder="WS-M-000123"
            value={consume.volumeCode}
            onChange={e => setConsume({ ...consume, volumeCode: e.target.value })}
          />
          <p className="-mt-2 text-xs text-gray-500">
            Com o volume, material, lote e dono vêm dele. Sem ele, escolha o material — mas aí o
            consumo fica sem lote e a rastreabilidade daquele apontamento não fecha.
          </p>

          {!consume.volumeCode && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Material da ficha</label>
              <select
                className={selectClass}
                value={consume.materialId}
                onChange={e => setConsume({ ...consume, materialId: e.target.value })}
              >
                <option value="">Selecione...</option>
                {order.items.map(it => (
                  <option key={it.materialId} value={it.materialId}>
                    {it.materialCode} — {it.materialName} (falta {it.remaining})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={consume.quantity}
              onChange={e => setConsume({ ...consume, quantity: e.target.value })}
              required
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                className={selectClass}
                value={consume.kind}
                onChange={e => setConsume({ ...consume, kind: e.target.value as 'consumo' | 'perda' })}
              >
                <option value="consumo">Consumo</option>
                <option value="perda">Perda</option>
              </select>
            </div>
          </div>

          <Input
            label={consume.kind === 'perda' ? 'Motivo da perda' : 'Observação'}
            value={consume.reason}
            onChange={e => setConsume({ ...consume, reason: e.target.value })}
            placeholder={consume.kind === 'perda' ? 'vidro quebrado no envase' : ''}
            required={consume.kind === 'perda'}
          />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setConsumeOpen(false)}>Cancelar</Button>
            <Button
              type="submit"
              loading={consumeMutation.isPending}
              disabled={!consume.volumeCode && !consume.materialId}
            >
              Apontar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal title="Encerrar ordem" open={closeOpen} onClose={() => setCloseOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); closeMutation.mutate() }}
        >
          <p className="text-sm text-gray-600">
            Encerrar é o momento em que o produto acabado passa a existir, no lote{' '}
            <span className="font-mono font-semibold">{order.lotNumber ?? '—'}</span>. A reserva não
            consumida volta para as outras ordens.
          </p>
          <Input
            label="Quantidade produzida"
            type="number"
            min={0}
            value={qtyProduced}
            onChange={e => setQtyProduced(e.target.value)}
            required
          />

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
              checked={sendToStock}
              onChange={e => setSendToStock(e.target.checked)}
            />
            <span>
              Dar entrada no estoque do WsFull
              <span className="block text-xs text-gray-500">
                Só vale se a marca também for cliente do fulfillment. O produto entra com o lote e a
                validade desta ordem, pelo mesmo caminho do recebimento.
              </span>
            </span>
          </label>

          {sendToStock && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Onde foi guardado</label>
              <select
                className={selectClass}
                value={stockLocationId}
                onChange={e => setStockLocationId(e.target.value)}
              >
                <option value="">GERAL — fracionamento, vendável agora (padrão)</option>
                {locations.filter(l => l.active && l.code !== 'GERAL').map(l => (
                  <option key={l.id} value={l.id}>{l.code} — {l.zone}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                A tela de Saldos só conta o <b>GERAL</b> (vendável agora) e os <b>paletes</b>.
                Produto acabado em qualquer outro endereço entra no estoque mas não aparece
                para quem vende nem para quem separa.
              </p>
            </div>
          )}
          {!canClose && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Há perda aguardando decisão — o encerramento vai ser recusado até um admin resolver.
            </p>
          )}
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setCloseOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={closeMutation.isPending}>Encerrar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
